import { Controller } from "@hotwired/stimulus"

// Calculates and displays duration between consecutive status/warning events
export default class extends Controller {
  static targets = ["row"]

  connect() {
    // Calculate durations immediately
    this.calculateDurations()
  }

  disconnect() {
    // Cleanup if needed
  }

  rowTargetConnected() {
    // Recalculate when a new row is added via infinite scroll
    if (!this.isCalculating) {
      this.scheduleCalculation()
    }
  }

  scheduleCalculation() {
    // Debounce calculations to avoid multiple rapid recalculations
    clearTimeout(this.calculationTimeout)
    this.calculationTimeout = setTimeout(() => {
      this.calculateDurations()
    }, 100)
  }

  calculateDurations() {
    if (this.isCalculating) return
    
    this.isCalculating = true
    const rows = Array.from(this.rowTargets)
    
    // Add day separators
    this.addDaySeparators(rows)
    
    // Group rows by event type (status or warning)
    const statusRows = rows.filter(row => row.dataset.eventType === 'status')
    const warningRows = rows.filter(row => row.dataset.eventType === 'warning')
    
    // Calculate durations for each group independently
    this.calculateGroupDurations(statusRows, 'status-duration')
    this.calculateGroupDurations(warningRows, 'warning-duration')
    
    this.isCalculating = false
  }

  addDaySeparators(rows) {
    // Remove existing separators first
    const existingSeparators = this.element.querySelectorAll('.day-separator')
    existingSeparators.forEach(el => el.remove())
    
    if (rows.length === 0) return
    
    let previousDate = null
    
    rows.forEach((row, index) => {
      if (!row.dataset.timestamp) return
      
      const currentDate = new Date(row.dataset.timestamp).toDateString()
      
      if (previousDate && previousDate !== currentDate) {
        // Day changed - insert separator before this row
        const separator = document.createElement('tr')
        separator.className = 'day-separator'
        separator.innerHTML = `
          <td colspan="8" class="px-4 py-2 bg-gray-50 border-y border-gray-200">
            <div class="flex items-center justify-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
              <span class="flex-1 h-px bg-gray-200"></span>
              <span>${this.formatDate(row.dataset.timestamp)}</span>
              <span class="flex-1 h-px bg-gray-200"></span>
            </div>
          </td>
        `
        row.parentNode.insertBefore(separator, row)
      }
      
      previousDate = currentDate
    })
  }

  formatDate(isoString) {
    const date = new Date(isoString)
    const options = { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }
    return date.toLocaleDateString('en-US', options)
  }

  calculateGroupDurations(rows, cellClass) {
    rows.forEach((row, index) => {
      const durationCell = row.querySelector(`.${cellClass}`)
      if (!durationCell) return

      if (index === 0) {
        // First (most recent) row - duration from this timestamp until now
        const timestamp = new Date(row.dataset.timestamp)
        const now = new Date()
        const durationMinutes = (now - timestamp) / 1000 / 60
        const compact = this.formatDurationCompact(durationMinutes)
        const full = this.formatDurationFull(durationMinutes)
        
        durationCell.innerHTML = `
          <div class="relative group inline-block">
            <span class="cursor-help">${compact}</span>
            <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50 pointer-events-none">
              ${full}
              <div class="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                <div class="border-4 border-transparent border-t-gray-900"></div>
              </div>
            </div>
          </div>
        `
        durationCell.classList.add('text-blue-600', 'font-medium')
      } else {
        // Other rows - duration from this timestamp until the previous (more recent) row
        const currentTime = new Date(row.dataset.timestamp)
        const previousTime = new Date(rows[index - 1].dataset.timestamp)
        const durationMinutes = (previousTime - currentTime) / 1000 / 60
        const compact = this.formatDurationCompact(durationMinutes)
        const full = this.formatDurationFull(durationMinutes)
        
        durationCell.innerHTML = `
          <div class="relative group inline-block">
            <span class="cursor-help">${compact}</span>
            <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50 pointer-events-none">
              ${full}
              <div class="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                <div class="border-4 border-transparent border-t-gray-900"></div>
              </div>
            </div>
          </div>
        `
        durationCell.classList.remove('text-blue-600', 'font-medium')
      }
    })
  }

  formatDurationCompact(minutes) {
    if (minutes < 0) return '-'
    
    const totalSeconds = Math.round(minutes * 60)
    
    if (totalSeconds < 60) {
      return `${totalSeconds}s`
    }
    
    if (totalSeconds < 3600) {
      const mins = Math.floor(totalSeconds / 60)
      return `${mins}m`
    }
    
    if (totalSeconds < 86400) { // Less than 24 hours
      const hours = Math.floor(totalSeconds / 3600)
      const mins = Math.floor((totalSeconds % 3600) / 60)
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
    }
    
    // Days and hours
    const days = Math.floor(totalSeconds / 86400)
    const hours = Math.floor((totalSeconds % 86400) / 3600)
    return hours > 0 ? `${days}d ${hours}h` : `${days}d`
  }

  formatDurationFull(minutes) {
    if (minutes < 0) return '-'
    
    const totalSeconds = Math.round(minutes * 60)
    
    if (totalSeconds < 60) {
      return `${totalSeconds} seconds`
    }
    
    const days = Math.floor(totalSeconds / 86400)
    const hours = Math.floor((totalSeconds % 86400) / 3600)
    const mins = Math.floor((totalSeconds % 3600) / 60)
    const secs = totalSeconds % 60
    
    let parts = []
    
    if (days > 0) {
      parts.push(`${days} day${days !== 1 ? 's' : ''}`)
    }
    if (hours > 0) {
      parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`)
    }
    if (mins > 0) {
      parts.push(`${mins} minute${mins !== 1 ? 's' : ''}`)
    }
    if (secs > 0) {
      parts.push(`${secs} second${secs !== 1 ? 's' : ''}`)
    }
    
    return parts.join(', ')
  }
}
